// =====================================================
// 📁 QuotationHistoryPage.tsx
// =====================================================

import React, { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';

import AdvancedRecordTable from '../../../components/Tables/advancedRecord/AdvancedRecordTable';

import { useCustomer } from '../../../hooks/useCustomer';

import { useEmployee } from '../../../hooks/employee/useEmployee';

import useQuotationHistory from '../../../hooks/sale/useQuotationHistory';

import { getQuotationHistoryFilters } from '../../../features/quotation/history/quotationHistoryFilters';

import { buildQuotationHistoryFilterPayload } from '../../../features/quotation/history/quotationHistoryFilterUtils';
import { getQuotationHistoryTableActions } from '../../../features/quotation/history/quotationHistoryTableActions';
import { quotationHistoryColumns } from '../../../features/quotation/history/quotationHistoryColumns';
import useQuotationPdf from '../../../hooks/pdf/useQuotationPdf';
import { useQuotation } from '../../../hooks/sale/useQuotation';
import { useQuotationPermissions } from '../../../hooks/sale/useQuotationPermissions';
import QuotationViewModal from '../../../components/Sales/quotation/modal/view/QuotationViewModal';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';
import ProcessQuotationModal from '../../../components/Sales/quotation/modal/ProcessQuotationModal';
import { EmployeeFilterType } from '../../../services/employees/EmployeeService';

/**
 * 📚 Historial de Cotizaciones
 */
const QuotationHistoryPage: React.FC = () => {
  // =====================================================
  // 🔹 Router
  // =====================================================
  const { page } = useParams();

  const currentPage = page ? parseInt(page, 10) : 0;

  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const {
    search,

    filteredQuotationList,

    paginator,

    pageSize,

    updatePageSize,

    clearFilteredList,

    cancelQuotation,
  } = useQuotationHistory();

  const {
    findDetailById,

    selectedQuotation,

    showQuotationModal,
    closeQuotationModal,

    showProcessModal,
    closeProcessModal,

    processQuotation,

    handlerSelectQuotation,
    openProcessModal,
  } = useQuotation();

  const {
    handlerGetCustomersForDropdown,

    customersDropdown,
  } = useCustomer();

  const {
    employeeDropdown,

    fetchEmployeesDropdown,
  } = useEmployee();

  const { handleDownloadQuotationPdf } = useQuotationPdf();

  const permissions = useQuotationPermissions();

  // =====================================================
  // 🔹 State
  // =====================================================

  const [filters, setFilters] = useState({});

  const [filtersApplied, setFiltersApplied] = useState(false);

  // =====================================================
  // 🔹 Filters
  // =====================================================

  const handleFilterChange = (newFilters: any) => {
    const payload = buildQuotationHistoryFilterPayload(newFilters);

    setFilters(payload);

    setFiltersApplied(true);

    search(payload, 0, pageSize);
  };

  // =====================================================
  // 🔹 Pagination
  // =====================================================

  const handlePageChange = (page: number) => {
    search(filters, page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    updatePageSize(size);

    search(filters, 0, size);
  };

  // =====================================================
  // 🔹 Effects
  // =====================================================

  useEffect(() => {
    handlerGetCustomersForDropdown();

    fetchEmployeesDropdown(EmployeeFilterType.QUOTATIONS);
  }, []);

  useEffect(() => {
    if (filtersApplied) {
      search(filters, currentPage, pageSize);
    }
  }, [currentPage]);

  // =====================================================
  // 🔹 Filters Config
  // =====================================================

  const filterFields = getQuotationHistoryFilters(
    customersDropdown,
    employeeDropdown
  );

  // =====================================================
  // 🔹 Actions
  // =====================================================

  const verCotizacion = (quotation: any) => {
    findDetailById(quotation.id);
  };

  const handleProcess = (quotation: any) => {
    handlerSelectQuotation(quotation);
    openProcessModal();
  };

  const getActions = (row: any) =>
    getQuotationHistoryTableActions({
      row,

      permissions,

      handlers: {
        onView: () => verCotizacion(row),

        onPdf: (quotation) => {
          handleDownloadQuotationPdf(quotation.id);
        },

        onCancel: cancelQuotation,

        onProcess: () => {
          handleProcess(row);
        },
      },
    });

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <AdvancedRecordTable
        columns={quotationHistoryColumns}
        data={filteredQuotationList}
        suppliers={customersDropdown}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        filtersConfig={filterFields}
        onFilterChange={handleFilterChange}
        getActions={getActions}
        paginator={paginator}
        onResetFilters={clearFilteredList}
        url="/quotation-history"
        pageSize={pageSize}
        noDataMessage="
          No hay cotizaciones registradas.
        "
        topRightAction={
          permissions.canCreate
            ? {
                label: 'Nueva Cotización',

                href: '/quotation-register',
              }
            : undefined
        }
      />

      <QuotationViewModal
        isOpen={showQuotationModal}
        onClose={closeQuotationModal}
        quotation={selectedQuotation}
      />

      <ProcessQuotationModal
        isOpen={showProcessModal}
        quotationId={selectedQuotation?.id || 0}
        loading={false}
        onClose={closeProcessModal}
        onProcess={async (quotationId, documentTypeId) => {
          await processQuotation(quotationId, documentTypeId);
          closeProcessModal();
        }}
      />
    </WorkspaceLayout>
  );
};

export default QuotationHistoryPage;
